import { CircleX, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import ExpandMapControl from '@/components/ExpandMapControl';
import Map from '@/components/Map';
import { usePhotos } from '@/hooks/usePhotos';
import { useUsers } from '@/hooks/useUsers';
import { useKeys } from '@/hooks/useKeys';
import useRoutePhoto from '@/hooks/useRoutePhoto';
import { Photo } from '@/lib/Photo';
import { Photos } from '@/lib/Photos';
import { User } from '@/lib/User';

import { CustomZoomSlider } from './CustomZoomSlider';
import { ResetViewControl } from './ResetViewControl';

export const PhotoPane = () => {
  const { photos: photoData, selectedPhoto: selectedPhotoData } = usePhotos();
  const { users } = useUsers();
  
  // Convert PhotoData to Photo instances and assign user data
  const photoInstances = photoData.map(data => {
    const photo = Photo.fromApi(data);
    const user = users.find(u => u.id.toString() === data.user_id.toString());
    if (user) {
      photo.user = User.fromApi(user);
    }
    return photo;
  });
  const photos = new Photos(photoInstances);
  
  const selectedPhoto = selectedPhotoData ? (() => {
    const photo = Photo.fromApi(selectedPhotoData);
    const user = users.find(u => u.id.toString() === selectedPhotoData.user_id.toString());
    if (user) {
      photo.user = User.fromApi(user);
    }
    return photo;
  })() : null;
  const { photo: routePhoto, done } = useRoutePhoto();
  const photo = routePhoto ?? selectedPhoto;
  console.log('photo', photo);
  console.log('photo.user', photo?.user);
  const navigate = useNavigate();
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  useKeys([
    ['Escape', () => onCloseClick()],
    ['ArrowRight', () => navigate(photos.nextUrl(photo))],
    ['ArrowLeft', () => navigate(photos.prevUrl(photo))],
    ['Enter', () => photos.redirectTo(photo)],
    ['m', () => setIsMapExpanded(!isMapExpanded)]
  ], (photo !== null));

  const onCloseClick = () => { navigate('/photos'); } // navigate(-1); }
  const onEditClick = () => { navigate(`${photo?.url()}/edit`); }

  const [showFullDate, setShowFullDate] = useState(false);

  // TODO: I don't think this works with selectedPhoto, but that should probably be removed anyway
  useEffect(() => {
    if (done && routePhoto === null) {
      navigate('/photos');
    }
  }, [done, routePhoto, navigate]);

  if (!photo) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-white rounded-lg">
        <p className="text-xl text-gray-600">No photo selected</p>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-white rounded-lg">
      <button
        onClick={onCloseClick}
        className="absolute z-10 flex items-center justify-center w-8 h-8 text-2xl text-white rounded-full cursor-pointer top-2 right-2 hover:opacity-75"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <CircleX className="w-8 h-8" />
      </button>
      <button
        onClick={onEditClick}
        className="absolute z-10 flex items-center justify-center w-8 h-8 text-2xl text-white rounded-full cursor-pointer top-2 left-2 hover:opacity-75"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <Pencil className="w-5 h-5" />
      </button>
      <div className="relative flex-4">
        <img
          src={photo.imageUrl}
          alt={photo.title}
          className="object-contain w-full h-full"
        />
      </div>
      <div className="flex-1 p-4 text-white" style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}>
        {(photo.title || photo.type) && (
          <h2 className="mb-2 text-xl font-semibold">{photo.type} {photo.title}</h2>
        )}
        {photo.description && (
          <p className="text-gray-300">{photo.description}</p>
        )}
        <div className="mt-4">
          <div className="flex justify-between">
            {photo.hasGeo() && (
              <div
                className="text-sm text-gray-300 align-baseline transition-colors cursor-pointer hover:text-white"
                onClick={() => setIsMapExpanded(!isMapExpanded)}
              >
                <span className="no-underline">{isMapExpanded ? '▼' : '▶'}</span>
                &nbsp;&nbsp;
                <span className="underline">{photo.locationString()}</span>
                {isMapExpanded && (
                  <span className="ml-2">
                    ({Math.abs(photo.geo.lat).toFixed(2)} {(photo.geo.lat > 0 ? 'N' : 'S')},&nbsp;
                    {Math.abs(photo.geo.lng).toFixed(2)} {(photo.geo.lng > 0 ? 'E' : 'W')})</span>
                )}
              </div>
            )}
            <div className="flex flex-col items-end">
              {photo.user && (
                <Link to={`/user/${photo.user.id}`} className="text-sm text-gray-300">
                  {photo.user.name}
                </Link>
              )}
              <div
                className={`text-sm text-gray-300 cursor-pointer hover:text-white ${!photo.hasGeo() ? 'w-full text-right' : ''}`}
                onClick={() => setShowFullDate(!showFullDate)}
                title={photo.fullDate()}
              >
                {showFullDate ? photo.fullDate() : photo.mediumDate()}
              </div>
            </div>
          </div>
          {photo.hasGeo() && isMapExpanded && (
            <div className="h-48 mt-2">
              <Map
                geo={photo.geo}
                extraControls={
                  <>
                    <ExpandMapControl
                      position="topright"
                      onClick={() => { navigate(`${photo.url()}/map`) }}
                    />
                    <CustomZoomSlider sliderPosition="leftside-short" />
                    <ResetViewControl position="bottomright" center={[photo.geo.lat, photo.geo.lng]} />
                  </>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
